/**
 * Magon — Gift Promotion (brinde automático)
 *
 * Suporta DUAS regras simultâneas, definidas no config JSON (data-gift-config):
 *   - type "value": adiciona o brinde quando o subtotal qualificado atinge o
 *     valor mínimo. Tem barra de progresso e toasts.
 *   - type "tag":   adiciona o brinde quando qualquer item do carrinho tem a
 *     tag configurada (ex: Masculino). Silenciosa (sem barra, sem toast).
 *
 * Cada brinde é um produto de R$ 0 com a tag 'brinde' (o preço não vem de
 * desconto do Shopify — o tema só adiciona/remove o produto). A tag do item
 * não vem no /cart.js, então resolvemos via seed SSR + /products/{handle}.js.
 *
 * Regras de UX (alinhadas com o cliente):
 *   - Caiu abaixo do gatilho → remove o brinde automaticamente.
 *   - Cliente removeu manualmente → não re-adiciona nesta sessão (sessionStorage
 *     por regra); re-adiciona em nova sessão.
 *   - Brinde com 2+ variantes → modal para escolher (sem variante padrão).
 *   - Sempre quantidade 1 por brinde.
 */
(function () {
  if (customElements.get('magon-gift-promotion')) return;

  // Cache de tags por productId (Set<string> em minúsculas). Compartilhado
  // entre instâncias (drawer + página de carrinho).
  var tagCache = new Map();

  // Serializa TODAS as mutações no carrinho (entre instâncias) para evitar
  // corridas que dupliquem brindes.
  var mutationChain = Promise.resolve();
  function serialize(task) {
    var next = mutationChain.then(task, task);
    mutationChain = next.catch(function () {});
    return next;
  }

  function lc(s) { return String(s == null ? '' : s).toLowerCase(); }

  class MagonGiftPromotion extends HTMLElement {
    connectedCallback() {
      var cfg = {};
      var configEl = this.querySelector('[data-gift-config]');
      try { cfg = JSON.parse(configEl ? configEl.textContent : '{}'); } catch (_) { cfg = {}; }

      this.rules = (Array.isArray(cfg.rules) ? cfg.rules : []).map(function (r) {
        return {
          type: r.type,
          giftProductId: parseInt(r.giftProductId, 10) || 0,
          giftProductHandle: r.giftProductHandle || '',
          giftVariantCount: parseInt(r.giftVariantCount, 10) || 0,
          lockedVariantId: parseInt(r.lockedVariantId, 10) || 0,
          variants: Array.isArray(r.giftVariants) ? r.giftVariants : [],
          thresholdCents: parseInt(r.thresholdCents, 10) || 0,
          qualifyingIds: Array.isArray(r.qualifyingProductIds) ? new Set(r.qualifyingProductIds) : null,
          tag: lc(r.tag).trim(),
          textProgress: r.textProgress || '',
          textUnlocked: r.textUnlocked || '',
          textRemoved: r.textRemoved || ''
        };
      }).filter(function (r) { return r.giftProductId; });

      if (!this.rules.length) return;

      this.giftProductIds = new Set(this.rules.map(function (r) { return r.giftProductId; }));
      this.valueRule = this.rules.find(function (r) { return r.type === 'value'; }) || null;
      this.hasTagRule = this.rules.some(function (r) { return r.type === 'tag'; });

      // Seed do cache de tags com os itens já presentes no carrinho (SSR)
      var seed = cfg.cartItemTags || {};
      Object.keys(seed).forEach(function (pid) {
        var arr = Array.isArray(seed[pid]) ? seed[pid] : [];
        tagCache.set(parseInt(pid, 10), new Set(arr.map(lc)));
      });

      this.fillEl = this.querySelector('[data-fill]');
      this.textEl = this.querySelector('[data-text]');

      this._wireModals();
      this._wireCartListener();
      this._wireManualRemoveListener();

      this._schedule(true);
    }

    /* -------------------------------- utils -------------------------------- */

    _money(cents) {
      var reais = (cents / 100).toFixed(2).replace('.', ',');
      return 'R$ ' + reais.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    _fetchCart() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
        if (!r.ok) throw new Error('Cart request failed: ' + r.status);
        return r.json();
      });
    }

    _dispatchCartUpdate(data) {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: {}, sourceId: 'magon-gift', data: { source: 'magon-gift', sections: data && data.sections } }
      }));
    }

    _toast(msg) {
      var el = document.querySelector('.magon-gift-toast');
      if (!el) {
        el = document.createElement('div');
        el.className = 'magon-gift-toast';
        document.body.appendChild(el);
      }
      el.textContent = msg;
      requestAnimationFrame(function () { el.classList.add('magon-gift-toast--visible'); });
      clearTimeout(el._timer);
      el._timer = setTimeout(function () { el.classList.remove('magon-gift-toast--visible'); }, 3000);
    }

    _sectionIds() {
      return Array.from(document.querySelectorAll('cart-items-component'))
        .map(function (el) { return el.dataset.sectionId; })
        .filter(Boolean);
    }

    _escape(s) {
      return String(s || '').replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    _dismissKey(rule) { return 'magon_gift_dismissed_' + rule.giftProductId; }

    _isDismissed(rule) {
      try { return sessionStorage.getItem(this._dismissKey(rule)) === '1'; } catch (_) { return false; }
    }

    _setDismissed(rule) {
      try { sessionStorage.setItem(this._dismissKey(rule), '1'); } catch (_) {}
    }

    _clearDismissed(rule) {
      try { sessionStorage.removeItem(this._dismissKey(rule)); } catch (_) {}
    }

    /* -------------------------- resolução de tags -------------------------- */

    _ensureTags(cart) {
      var missing = cart.items.filter(function (it) { return !tagCache.has(it.product_id); });
      if (!missing.length) return Promise.resolve();
      return Promise.all(missing.map(function (it) {
        return fetch('/products/' + it.handle + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) {
            var tags = (p && Array.isArray(p.tags)) ? p.tags : [];
            tagCache.set(it.product_id, new Set(tags.map(lc)));
          })
          .catch(function () { tagCache.set(it.product_id, new Set()); });
      }));
    }

    _cartHasTag(cart, tag) {
      var giftIds = this.giftProductIds;
      return cart.items.some(function (it) {
        if (giftIds.has(it.product_id)) return false;
        var tags = tagCache.get(it.product_id);
        return !!(tags && tags.has(tag));
      });
    }

    /* --------------------------- cálculo por regra --------------------------- */

    _qualifyingSubtotal(cart, rule) {
      var giftIds = this.giftProductIds;
      return cart.items.reduce(function (sum, item) {
        if (giftIds.has(item.product_id)) return sum;
        if (rule.qualifyingIds && !rule.qualifyingIds.has(item.product_id)) return sum;
        return sum + item.final_line_price;
      }, 0);
    }

    _giftLine(cart, rule) {
      return cart.items.find(function (i) { return i.product_id === rule.giftProductId; });
    }

    _ruleEligible(cart, rule) {
      if (rule.type === 'value') return this._qualifyingSubtotal(cart, rule) >= rule.thresholdCents;
      if (rule.type === 'tag') return this._cartHasTag(cart, rule.tag);
      return false;
    }

    /* ------------------------------ orquestração ------------------------------ */

    _schedule(external) {
      if (external) this._pendingMutations = true;
      if (this._locked) { this._rerun = true; return; }
      this._process();
    }

    async _process() {
      this._locked = true;
      try {
        do {
          this._rerun = false;
          var applyMut = this._pendingMutations;
          this._pendingMutations = false;
          await this._evaluate(applyMut);
        } while (this._rerun);
      } finally {
        this._locked = false;
      }
    }

    async _evaluate(applyMutations) {
      var cart;
      try { cart = await this._fetchCart(); } catch (_) { return; }
      if (this.hasTagRule) { try { await this._ensureTags(cart); } catch (_) {} }

      this._updateBar(cart);

      if (!applyMutations) return;

      var self = this;
      await serialize(async function () {
        var modalOpened = false;
        for (var i = 0; i < self.rules.length; i++) {
          var rule = self.rules[i];
          // Estado fresco a cada regra (a anterior pode ter mudado o carrinho)
          try { cart = await self._fetchCart(); } catch (_) { return; }
          if (rule.type === 'tag') { try { await self._ensureTags(cart); } catch (_) {} }

          var line = self._giftLine(cart, rule);
          var eligible = self._ruleEligible(cart, rule);
          var dismissed = self._isDismissed(rule);

          if (!eligible && line) {
            await self._removeGift(rule, line.key);
            continue;
          }
          if (eligible && !line && !dismissed) {
            var res = await self._tryAddGift(rule, modalOpened);
            if (res === 'modal') modalOpened = true;
          }
        }
      });
    }

    _updateBar(cart) {
      if (!this.valueRule || !this.fillEl || !this.textEl) return;
      var rule = this.valueRule;
      var subtotal = this._qualifyingSubtotal(cart, rule);
      var eligible = subtotal >= rule.thresholdCents;
      var pct = rule.thresholdCents > 0 ? Math.min(100, Math.round((subtotal * 100) / rule.thresholdCents)) : 100;
      this.fillEl.style.width = pct + '%';
      this.fillEl.classList.toggle('magon-gift-promo__fill--complete', eligible);
      if (eligible) {
        this.textEl.innerHTML = '<strong>' + this._escape(rule.textUnlocked) + '</strong>';
      } else {
        var missing = rule.thresholdCents - subtotal;
        var tpl = rule.textProgress || 'Faltam {{valor}}';
        var amountHtml = '<strong>' + this._money(missing) + '</strong>';
        this.textEl.innerHTML = tpl.replace('{{valor}}', amountHtml).replace('[amount]', amountHtml);
      }
    }

    /* ------------------------------ add/remove ------------------------------ */

    // Retorna 'modal' quando abriu o modal; true quando adicionou; false caso contrário.
    async _tryAddGift(rule, suppressModal) {
      if (rule.lockedVariantId) {
        var locked = rule.variants.find(function (v) { return v.id === rule.lockedVariantId; });
        if (locked && locked.available) { await this._postAdd(rule, locked.id); return true; }
        if (rule.type === 'value') this._toast('Brinde temporariamente esgotado.');
        return false;
      }
      var available = rule.variants.filter(function (v) { return v.available; });
      if (!available.length) {
        if (rule.type === 'value') this._toast('Brinde temporariamente esgotado.');
        return false;
      }
      if (available.length === 1 || rule.giftVariantCount <= 1) {
        await this._postAdd(rule, available[0].id);
        return true;
      }
      // 2+ variantes disponíveis → modal (evita abrir dois modais no mesmo ciclo)
      if (suppressModal) return false;
      this._openModal(rule);
      return 'modal';
    }

    async _postAdd(rule, variantId) {
      var body = { id: parseInt(variantId, 10), quantity: 1 };
      var sids = this._sectionIds();
      if (sids.length) body.sections = sids.join(',');
      try {
        var resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        if (!resp.ok) return;
        var data = await resp.json();
        this._clearDismissed(rule);
        if (rule.type === 'value' && rule.textUnlocked) this._toast(rule.textUnlocked);
        this._dispatchCartUpdate(data);
      } catch (_) { /* silencioso */ }
    }

    async _removeGift(rule, lineKey) {
      try {
        var body = { id: lineKey, quantity: 0 };
        var sids = this._sectionIds();
        if (sids.length) body.sections = sids.join(',');
        var resp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        if (!resp.ok) return;
        var data = await resp.json();
        if (rule.type === 'value' && rule.textRemoved) {
          var missing = rule.thresholdCents - this._qualifyingSubtotal(data, rule);
          var amt = this._money(Math.max(0, missing));
          this._toast(rule.textRemoved.replace('{{valor}}', amt).replace('[amount]', amt));
        }
        this._dispatchCartUpdate(data);
      } catch (_) { /* silencioso */ }
    }

    /* ------------------------------- modais ------------------------------- */

    _wireModals() {
      var self = this;
      this.modals = Array.prototype.slice.call(this.querySelectorAll('[data-gift-modal]'));
      this.modals.forEach(function (modal) {
        var ruleId = parseInt(modal.dataset.ruleProductId, 10) || 0;
        var rule = self.rules.find(function (r) { return r.giftProductId === ruleId; });
        modal.addEventListener('click', function (e) {
          var variantBtn = e.target.closest('[data-variant-id]');
          if (variantBtn && !variantBtn.disabled) {
            self._closeModal(modal);
            if (rule) {
              serialize(function () { return self._postAdd(rule, variantBtn.dataset.variantId); })
                .then(function () { self._schedule(true); });
            }
            return;
          }
          if (e.target.closest('[data-gift-modal-close]')) {
            self._closeModal(modal);
            if (rule) self._setDismissed(rule);
          }
        });
      });
    }

    _openModal(rule) {
      var modal = this.modals.find(function (m) { return (parseInt(m.dataset.ruleProductId, 10) || 0) === rule.giftProductId; });
      if (modal && typeof modal.showModal === 'function') { try { modal.showModal(); } catch (_) {} }
    }

    _closeModal(modal) {
      if (modal && typeof modal.close === 'function') { try { modal.close(); } catch (_) {} }
    }

    /* --------------------------- event wiring --------------------------- */

    _wireCartListener() {
      var self = this;
      this._onUpdate = function (e) {
        var external = !(e.detail && e.detail.data && e.detail.data.source === 'magon-gift');
        self._schedule(external);
      };
      document.addEventListener('cart:update', this._onUpdate);
    }

    /**
     * Detecta remoção manual de QUALQUER brinde (por regra) para não re-adicionar
     * na sessão. Delegation no document, capturando antes do request.
     */
    _wireManualRemoveListener() {
      var self = this;
      this._onManualClick = function (e) {
        var el = e.target;
        if (!el) return;
        for (var i = 0; i < self.rules.length; i++) {
          var rule = self.rules[i];
          var line = el.closest('[data-product-id], [data-line-item]');
          if (line && parseInt(line.dataset.productId, 10) === rule.giftProductId) {
            self._setDismissed(rule);
            return;
          }
          var btn = el.closest('cart-remove-button, [data-cart-remove]');
          if (btn) {
            var vId = parseInt(btn.dataset.variantId || btn.dataset.id || '0', 10);
            if (vId && rule.variants.some(function (v) { return v.id === vId; })) {
              self._setDismissed(rule);
              return;
            }
          }
        }
      };
      document.addEventListener('click', this._onManualClick, true);
    }

    disconnectedCallback() {
      if (this._onUpdate) document.removeEventListener('cart:update', this._onUpdate);
      if (this._onManualClick) document.removeEventListener('click', this._onManualClick, true);
    }
  }

  customElements.define('magon-gift-promotion', MagonGiftPromotion);
})();
